import { LuIcon, Menu, type MenuRootProps, type MenuVirtualTriggerProps } from "@overmap-ai/blocks"
import { type Issue, OrganizationAccessLevel } from "@overmap-ai/common"
import {
	selectIssueById,
	selectIssueTypePrioritiesOfIssueType,
	selectIssueTypeStatusesOfIssueType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import { memo, useCallback, useMemo } from "react"
import { useMap } from "react-map-gl/mapbox"

import { useLocationPicker } from "../../contexts/hooks"
import { useDeleteIssue, useIssue } from "../../hooks/issues"
import { useAssignees } from "../../hooks/users"
import { coordinatesToLiteral } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveProjectId } from "../../store/selectors"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { LocationGroup } from "../MenuGroups/LocationGroup"
import { AssigneeSelectGroup } from "../MenuSelectGroups/AssigneeSelectGroup"
import { IssuePrioritySelectGroup } from "../MenuSelectGroups/IssuePrioritySelectGroup"
import { IssueTypeStatusSelectGroup } from "../MenuSelectGroups/IssueTypeStatusSelectGroup"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

enum IssueMarkerContextMenuPages {
	INITIAL = "initial",
	PRIORITY = "priority",
	STATUS = "status",
	ASSIGNEE = "assignee",
	LOCATION = "location",
}

interface IssueMarkerContextMenuProps extends Omit<MenuRootProps, "children">, MenuVirtualTriggerProps {
	issueId: Issue["uuid"]
	onDelete?: () => void
}

export const IssueMarkerContextMenu = memo((props: IssueMarkerContextMenuProps) => {
	const { issueId, virtualElement, onDelete, disabled = false, ...rest } = props

	const issue = useAppSelector(selectIssueById(issueId))

	if (!issue) {
		throw new Error(`Expected issue to be defined, got ${issue}`)
	}

	const deleteIssue = useDeleteIssue()
	const openLocationPicker = useLocationPicker()
	const { current: map } = useMap()
	const navigate = useNavigate()
	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const allUsers = useAssignees(useShallowEqaulSelector(selectUsersOfProject(activeProjectId)))
	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issue.issue_type))
	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issue.issue_type))

	const {
		assignee,
		issueTypePriority,
		issueTypeStatus,
		handlePriorityChange,
		handleStatusChange,
		handleAssigneeChange,
	} = useIssue(issue)

	const issueLocation = useMemo(() => {
		return issue.geo_marker?.coordinates
	}, [issue.geo_marker?.coordinates])

	const handleOpenLocationPicker = useCallback(() => {
		// need to center on map before changing location since the LocationMarker is rendered centered in the viewport
		if (issueLocation) {
			map?.panTo(coordinatesToLiteral(issueLocation))
		}
		openLocationPicker((close) => ({
			disableSkip: true,
			onConfirm: (coordinates) => {
				close()
				if (!coordinates) return
				void sdk.issues.update({
					uuid: issue.uuid,
					geo_marker: turf.point(coordinates.toArray()).geometry,
				})
			},
			onCancel: close,
		}))
	}, [issue.uuid, issueLocation, map, openLocationPicker])

	const handleViewIssue = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/issues/$issueId",
			params: (prev) => ({
				...prev,
				issueId: issue.uuid,
			}),
		})
	}, [issue.uuid, navigate])

	return (
		<Menu.Root modal={false} {...rest}>
			<Menu.VirtualTrigger virtualElement={virtualElement} disabled={disabled} />
			<Menu.Content size="sm">
				<Menu.Label>
					<IssueTypeIcon issueTypeId={issue.issue_type} inline />
					<IssueIndexLabel highContrast issueId={issue.uuid} />
				</Menu.Label>
				<Menu.Separator />
				<Menu.Pages defaultPage={IssueMarkerContextMenuPages.INITIAL}>
					<Menu.PageContent page={IssueMarkerContextMenuPages.INITIAL}>
						<Menu.Item onSelect={handleViewIssue}>
							<LuIcon icon={modelIconMapping.expand} />
							View
						</Menu.Item>
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
							<Menu.Separator />
							<Menu.PageTrigger page={IssueMarkerContextMenuPages.PRIORITY}>
								<LuIcon icon={modelIconMapping.priority} />
								Priority
							</Menu.PageTrigger>
							<Menu.PageTrigger page={IssueMarkerContextMenuPages.STATUS}>
								<LuIcon icon={modelIconMapping.status} />
								Status
							</Menu.PageTrigger>
							<Menu.PageTrigger page={IssueMarkerContextMenuPages.ASSIGNEE}>
								<LuIcon icon={modelIconMapping.user} />
								Assignee
							</Menu.PageTrigger>
						</OrganizationAccessRequired>
						<Menu.PageTrigger page={IssueMarkerContextMenuPages.LOCATION}>
							<LuIcon icon={modelIconMapping.location} />
							Location
						</Menu.PageTrigger>
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
							<Menu.Separator />
							<Menu.Item onSelect={() => deleteIssue(issue?.uuid, onDelete)}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</OrganizationAccessRequired>
					</Menu.PageContent>
					<Menu.PageContent page={IssueMarkerContextMenuPages.PRIORITY}>
						<Menu.Scroll>
							<IssuePrioritySelectGroup
								required
								value={issueTypePriority}
								onValueChange={handlePriorityChange}
								priorities={issueTypePriorities}
							/>
						</Menu.Scroll>
					</Menu.PageContent>
					<Menu.PageContent page={IssueMarkerContextMenuPages.STATUS}>
						<Menu.Scroll>
							<IssueTypeStatusSelectGroup
								required
								value={issueTypeStatus}
								onValueChange={handleStatusChange}
								issueTypeStatuses={issueTypeStatuses}
							/>
						</Menu.Scroll>
					</Menu.PageContent>
					<Menu.PageContent page={IssueMarkerContextMenuPages.ASSIGNEE}>
						<Menu.Scroll>
							<AssigneeSelectGroup
								users={allUsers}
								required
								value={assignee ?? null}
								onValueChange={handleAssigneeChange}
							/>
						</Menu.Scroll>
					</Menu.PageContent>
					<Menu.PageContent page={IssueMarkerContextMenuPages.LOCATION}>
						<LocationGroup coordinates={issueLocation} onLocationChange={handleOpenLocationPicker} />
					</Menu.PageContent>
				</Menu.Pages>
			</Menu.Content>
		</Menu.Root>
	)
})
IssueMarkerContextMenu.displayName = "IssueMarkerContextMenu"
